import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Fallback preferences for a user whose doc has no explicit value.
//
// This is the LEGACY default and must stay 'ft-in'. Existing accounts created
// before inches became the default have no depthFormat field of their own, so
// they read through to here — changing it would silently reformat every screen
// for people who never asked for it.
//
// New accounts get NEW_ACCOUNT_PREFS written explicitly at sign-up instead, so
// the two populations are distinguished by what is stored, not by a guess.
const DEFAULTS = {
  depthFormat: 'ft-in',  // 'ft-in' (8 ft 3 in) | 'ft-decimal' (8.3 ft) | 'inches' (99 in)
};

// Written to users/{uid} when an account is created. Inches only: it is the
// number already printed on a tape measure, and it is ONE field to fill in
// during setup instead of two.
export const NEW_ACCOUNT_PREFS = {
  depthFormat: 'inches',
};

export default function useUserPrefs(user) {
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  // Load prefs once when user changes
  useEffect(() => {
    if (!user) {
      setPrefs(DEFAULTS);
      setLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        if (snap.exists()) {
          setPrefs({ ...DEFAULTS, ...snap.data() });
        } else {
          setPrefs(DEFAULTS);
        }
      } catch (err) {
        console.error('useUserPrefs load error:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Update a single pref locally and persist to Firestore
  const updatePref = useCallback(async (key, value) => {
    if (!user) return;
    setPrefs(p => ({ ...p, [key]: value }));   // optimistic update
    try {
      await setDoc(
        doc(db, 'users', user.uid),
        { [key]: value },
        { merge: true }                         // creates the doc if missing
      );
    } catch (err) {
      console.error('useUserPrefs save error:', err);
    }
  }, [user]);

  return { prefs, loaded, updatePref };
}